import React, { useEffect, useState } from "react";
import {
  ALL_COLLABORATORS_LABEL,
  COMPETENCE_CENTER_LABEL,
  DISTRIBUTION_CHART_TITLE_LABEL,
  MATRIX_PERFORMANCE_DOWNLOAD_TITLE_LABEL,
  PRACTICE_LABEL,
  START_PERFORMANCE_COMMITTEE_LABEL,
} from "../../../constants/Dictionary";
import { PotentialMatrixFilters } from "../../../hooks/usePotentialMatrixFilters.hook";
import { PotentialMatrixFilterKeyEnum } from "../../../types/api/filters.type";
import { PotentialMatrixTabLayout } from "../PotentialMatrixTabLayout/PotentialMatrixTabLayout";
import {
  PerfomanceChart,
  PerformanceChartImpactStats,
} from "./PerformanceChart/PerfomanceChart";
import { useAppSelector } from "../../../hooks/store.hook";
import { MatrixPerformanceGrid } from "../../../components/MatrixPerformanceGrid/MatrixPerformanceGrid";
import {
  MatrixSizes,
  MatrixViewModeV3,
} from "../../../constants/PotentialMatrixConstants";
import {
  AlignmentEnum,
  FilterProps,
  FontWeightEnum,
} from "../../../types/AppTypes";
import { BodyTyp } from "../../../components/Typography/Typography";
import { MatrixPerformanceItem } from "../../../types/api/potentialMatrix.type";
import {
  useMatrixPerformanceCollaborators,
  usePotentialMatrixPerformance,
} from "../../../hooks/potentialMatrix/usePotentialMatrixPerfomance.hook";
import { DoubleStructure } from "../../../components/core/DoubleStructure/DoubleStructure";
import { PerformanceMatrixDetails } from "../PerformanceMatrixDetails/PerformanceMatrixDetails";
import MatrixForDownload from "../../PotentialMatrix/MatrixForDownload/MatrixForDownload";
import { useMatrixDownloadSubtitle } from "../../../hooks/potentialMatrix/useMatrixDownloadSubtitle.hook";

type PerformanceMatrixProps = {
  filters: PotentialMatrixFilters;
  handleFilters: (
    key: PotentialMatrixFilterKeyEnum,
    value: PotentialMatrixFilters[PotentialMatrixFilterKeyEnum],
  ) => void;
  previousYear?: number;
  selectedViewMode: MatrixViewModeV3;
  setSelectedViewMode: (mode: MatrixViewModeV3) => void;
  createQueryParams: () => FilterProps[];
  onDetailsViewChange?: (isOpen: boolean) => void;
  isDownloadEnabled?: boolean;
  downloadRef?: React.Ref<HTMLDivElement>;
  downloadData?: MatrixPerformanceItem[];
  showDownloadPreview?: boolean;
};

export const MOCK_DATA: PerformanceChartImpactStats[] = [
  {
    impact: "Conservative",
    ranges: [
      {
        initials: "L",
        name: "Low",
        total: 200,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 30 },
          { internalGrade: "2 - Consultant", count: 50 },
          { internalGrade: "3 - Senior Consultant", count: 30 },
          { internalGrade: "4 - Manager", count: 50 },
          { internalGrade: "5 - Senior Manager", count: 40 },
        ],
      },
      {
        initials: "M",
        name: "Medium",
        total: 20,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 4 },
          { internalGrade: "2 - Consultant", count: 5 },
          { internalGrade: "3 - Senior Consultant", count: 3 },
          { internalGrade: "4 - Manager", count: 4 },
          { internalGrade: "5 - Senior Manager", count: 4 },
        ],
      },
      {
        initials: "MH",
        name: "Medium High",
        total: 75,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 17 },
          { internalGrade: "2 - Consultant", count: 16 },
          { internalGrade: "3 - Senior Consultant", count: 14 },
          { internalGrade: "4 - Manager", count: 12 },
          { internalGrade: "5 - Senior Manager", count: 16 },
        ],
      },
      {
        initials: "H",
        name: "High",
        total: 42,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 9 },
          { internalGrade: "2 - Consultant", count: 11 },
          { internalGrade: "3 - Senior Consultant", count: 7 },
          { internalGrade: "4 - Manager", count: 8 },
          { internalGrade: "5 - Senior Manager", count: 7 },
        ],
      },
    ],
  },
  {
    impact: "Standard",
    ranges: [
      {
        initials: "L",
        name: "Low",
        total: 18,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 5 },
          { internalGrade: "2 - Consultant", count: 4 },
          { internalGrade: "3 - Senior Consultant", count: 3 },
          { internalGrade: "4 - Manager", count: 2 },
          { internalGrade: "5 - Senior Manager", count: 4 },
        ],
      },
      {
        initials: "M",
        name: "Medium",
        total: 70,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 18 },
          { internalGrade: "2 - Consultant", count: 14 },
          { internalGrade: "3 - Senior Consultant", count: 12 },
          { internalGrade: "4 - Manager", count: 11 },
          { internalGrade: "5 - Senior Manager", count: 15 },
        ],
      },
      {
        initials: "MH",
        name: "Medium High",
        total: 80,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 22 },
          { internalGrade: "2 - Consultant", count: 19 },
          { internalGrade: "3 - Senior Consultant", count: 13 },
          { internalGrade: "4 - Manager", count: 10 },
          { internalGrade: "5 - Senior Manager", count: 16 },
        ],
      },
      {
        initials: "H",
        name: "High",
        total: 63,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 14 },
          { internalGrade: "2 - Consultant", count: 12 },
          { internalGrade: "3 - Senior Consultant", count: 9 },
          { internalGrade: "4 - Manager", count: 15 },
          { internalGrade: "5 - Senior Manager", count: 13 },
        ],
      },
    ],
  },
  {
    impact: "Challenging",
    ranges: [
      {
        initials: "L",
        name: "Low",
        total: 23,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 5 },
          { internalGrade: "2 - Consultant", count: 4 },
          { internalGrade: "3 - Senior Consultant", count: 4 },
          { internalGrade: "4 - Manager", count: 4 },
          { internalGrade: "5 - Senior Manager", count: 4 },
        ],
      },
      {
        initials: "M",
        name: "Medium",
        total: 40,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 10 },
          { internalGrade: "2 - Consultant", count: 6 },
          { internalGrade: "3 - Senior Consultant", count: 6 },
          { internalGrade: "4 - Manager", count: 6 },
          { internalGrade: "5 - Senior Manager", count: 12 },
        ],
      },
      {
        initials: "MH",
        name: "Medium High",
        total: 53,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 9 },
          { internalGrade: "2 - Consultant", count: 13 },
          { internalGrade: "3 - Senior Consultant", count: 11 },
          { internalGrade: "4 - Manager", count: 7 },
          { internalGrade: "5 - Senior Manager", count: 13 },
        ],
      },
      {
        initials: "H",
        name: "High",
        total: 72,
        grades: [
          { internalGrade: "1 - Junior Consultant", count: 15 },
          { internalGrade: "2 - Consultant", count: 20 },
          { internalGrade: "3 - Senior Consultant", count: 15 },
          { internalGrade: "4 - Manager", count: 14 },
          { internalGrade: "5 - Senior Manager", count: 8 },
        ],
      },
    ],
  },
];

export const PerformanceMatrix = ({
  filters,
  handleFilters,
  previousYear,
  selectedViewMode,
  setSelectedViewMode,
  createQueryParams,
  onDetailsViewChange,
  isDownloadEnabled = false,
  downloadRef,
  downloadData,
  showDownloadPreview = false,
}: PerformanceMatrixProps) => {
  const { config } = useAppSelector((state) => state.config);
  const configExists = config && config.length > 0;
  const [itemDetails, setItemDetails] = useState<MatrixPerformanceItem | null>(
    null,
  );
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isAnimatingClose, setIsAnimatingClose] = useState(false);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);

  const { data: performanceMatrixData, isFetching } =
    usePotentialMatrixPerformance({
      createQueryParams,
      filters,
      enabled: itemDetails === null && configExists,
    });

  const hasData = MOCK_DATA.length > 0;

  const {
    data: matrixPerformanceCollaborators,
    initLoading: matrixPerformanceCollaboratorsLoading,
    canLoadMore: canLoadMoreCollaborators,
    fetchMore: fetchMoreCollaborators,
    fetchingMore: fetchingMoreCollaborators,
    clearSearch: clearMatrixCollaboratorsSearch,
    setSearch: setMatrixCollaboratorsSearchValue,
  } = useMatrixPerformanceCollaborators({
    filters,
    enabled: showSearch && searchValue !== "",
  });

  const { practiceLabel, ccValue } = useMatrixDownloadSubtitle({ filters });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setMatrixCollaboratorsSearchValue(value);

    if (filters.collaboratorId) {
      handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    }
  };

  const toggleSearchInput = () => {
    setShowSearch(false);
    if (filters.collaboratorId === null) {
      setSearchValue("");
      clearMatrixCollaboratorsSearch();
    }
  };

  const handleClearSearch = () => {
    handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    setSearchValue("");
    clearMatrixCollaboratorsSearch();
    setShowSearch(false);
  };

  const handleSelectCollaborator = (
    collaboratorId: number,
    collaboratorLabel: string,
  ) => {
    setSearchValue(collaboratorLabel);
    setMatrixCollaboratorsSearchValue(collaboratorLabel);
    handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, collaboratorId);
    setShowSearch(false);
  };

  const handleCloseDetails = () => {
    setIsAnimatingClose(true);

    const timeout = setTimeout(() => {
      setIsAnimatingClose(false);
      setItemDetails(null);
    }, 500);

    return () => clearTimeout(timeout);
  };

  const handleClickMatrixItem = (item: MatrixPerformanceItem) => {
    setIsAnimatingOpen(true);
    setItemDetails(item);

    const timeout = setTimeout(() => {
      setIsAnimatingOpen(false);
    }, 500);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    if (!itemDetails) return;
    setItemDetails(itemDetails);
    setIsAnimatingClose(false);
  }, [itemDetails]);

  useEffect(() => {
    onDetailsViewChange?.(!!itemDetails);
  }, [itemDetails, onDetailsViewChange]);

  return (
    <PotentialMatrixTabLayout
      headerProps={{
        filters,
        handleFilters,
        previousYear,
        selectedViewMode,
        setSelectedViewMode,
        isIconSwitchEnabled: true,
        collaboratorSearchProps: {
          isVisible: showSearch,
          setIsVisible: setShowSearch,
          onClose: toggleSearchInput,
          value: searchValue,
          onChange: handleSearchChange,
          onClear: handleClearSearch,
          onSelectCollaborator: handleSelectCollaborator,
          data: matrixPerformanceCollaborators.map((item) => item.collaborator),
          loading: matrixPerformanceCollaboratorsLoading,
          fetchingMore: fetchingMoreCollaborators,
          canLoadMore: canLoadMoreCollaborators,
          fetchMore: fetchMoreCollaborators,
          showEmptyMessage: !filters.collaboratorId,
        },
        ...(selectedViewMode === MatrixViewModeV3.GLOBAL && {
          title: (
            <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
              <BodyTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
                {DISTRIBUTION_CHART_TITLE_LABEL}
              </BodyTyp>
            </DoubleStructure>
          ),
        }),
        showInternalGradeFilters: hasData,
      }}
      footerProps={{
        onClick: () =>
          console.log(
            "consentire l'avvio della sezione di development commitee ",
          ),
        label: START_PERFORMANCE_COMMITTEE_LABEL,
      }}
    >
      {selectedViewMode === MatrixViewModeV3.GLOBAL && (
        <PerfomanceChart data={MOCK_DATA} />
      )}
      {selectedViewMode === MatrixViewModeV3.COLLABORATOR &&
        (itemDetails ? (
          <PerformanceMatrixDetails
            item={itemDetails}
            onClose={handleCloseDetails}
            filters={filters}
            yearSelected={Number(filters.year)}
            onClickResume={() => console.log("aprire resume")}
            matrixData={performanceMatrixData ?? []}
            isAnimatingClose={isAnimatingClose}
          />
        ) : (
          <MatrixPerformanceGrid
            yearSelected={Number(filters.year)}
            data={performanceMatrixData ?? []}
            onClickItem={handleClickMatrixItem}
            size={MatrixSizes.BIG}
            selectedItem={itemDetails ?? undefined}
            loading={isFetching}
            viewMode={selectedViewMode}
            isAnimatingOpen={isAnimatingOpen}
          />
        ))}
      {isDownloadEnabled && (
        <MatrixForDownload
          ref={downloadRef}
          year={filters.year}
          title={MATRIX_PERFORMANCE_DOWNLOAD_TITLE_LABEL}
          subtitleItems={[
            {
              label: PRACTICE_LABEL,
              value: `${practiceLabel} | ${ALL_COLLABORATORS_LABEL}`,
            },
            { label: COMPETENCE_CENTER_LABEL, value: ccValue },
          ]}
        >
          <MatrixPerformanceGrid
            yearSelected={Number(filters.year)}
            data={downloadData ?? []}
            size={MatrixSizes.BIG}
            showDownloadPreview={showDownloadPreview}
          />
        </MatrixForDownload>
      )}
    </PotentialMatrixTabLayout>
  );
};
